import { useState } from 'react';

import type { HistoryItem, InteractionHistoryActions } from '@poseidon/components/ContactDetail';
import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';

import { SequenceCanvasHeader } from '~/components/Sequence/SequenceCanvasHeader';
import { SequenceGraphEditor } from '~/components/Sequence/SequenceGraphEditor';
import { useContactSequenceEnrollments } from '~/components/Sequence/useContactSequenceEnrollments';

interface Props {
  teamId: string;
  /** The open contact's public id. */
  contactId: string;
  /** The contact's history (oldest first) and what each entry can do — drawn at the top of the canvas. */
  history: HistoryItem[];
  historyActions: InteractionHistoryActions;
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
}

/**
 * The interact tab's GRAPH view: the whole tab as ONE canvas — the contact's history flowing down into the
 * locked graph of their sequence. It fills the section exactly (no page scroll around it), so the wheel /
 * trackpad pans it and nothing else scrolls; it opens at 100% on where they are now. A contact in more than one
 * sequence picks which one at the top-left, where a paused one also says so.
 */
export function ContactSequenceCanvas({ teamId, contactId, history, historyActions, variables, documents }: Props) {
  const { enrollments, saveStepContent, completeManualStep } = useContactSequenceEnrollments(teamId, contactId);
  const [pickedSequenceId, setPickedSequenceId] = useState<string | null>(null);
  const enrollment = enrollments.find((e) => e.sequencePublicId === pickedSequenceId) ?? enrollments[0];
  if (enrollment == null) return null;
  return (
    <div className="relative size-full">
      <SequenceCanvasHeader
        sequences={enrollments.map((e) => ({ id: e.sequencePublicId, name: e.sequenceName }))}
        selectedId={enrollment.sequencePublicId}
        onSelect={setPickedSequenceId}
        paused={enrollment.pausedAt != null}
      />
      <SequenceGraphEditor
        // A fresh canvas per sequence: its opening frame (on "now") belongs to the graph it opened on.
        key={enrollment.enrollmentId}
        readOnly
        fillsSection
        teamId={teamId}
        sequenceId={enrollment.sequencePublicId}
        initialSteps={enrollment.steps}
        overrides={enrollment.overrides}
        stepStatus={enrollment.stepStatus}
        currentNodeId={enrollment.currentNodeId}
        onSaveStepContent={saveStepContent(enrollment)}
        onCompleteManualStep={completeManualStep(enrollment)}
        variables={variables}
        documents={documents}
        history={{ items: history, actions: historyActions }}
      />
    </div>
  );
}
